// Mirrors the API's limits (api-service/utils/validation.py) so people hear about a problem before they submit.
export const MAX_NAME_LENGTH = 100;
export const MAX_ABOUT_LENGTH = 500;
export const MAX_MESSAGE_LENGTH = 2000;

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export type FieldErrors<Field extends string> = Partial<Record<Field, string>>;

export interface AccessRequestFields {
  name: string;
  email: string;
  about: string;
  consent: boolean;
}

export interface ContactMessageFields {
  name: string;
  email: string;
  message: string;
}

function validateName(name: string): string | undefined {
  if (!name.trim()) return "Please tell us your name.";
  if (name.trim().length > MAX_NAME_LENGTH) return `Please keep your name to ${MAX_NAME_LENGTH} characters.`;
  return undefined;
}

function validateEmail(email: string): string | undefined {
  if (!email.trim()) return "Please enter your email address.";
  if (!EMAIL_PATTERN.test(email.trim())) return "That doesn't look like an email address.";
  return undefined;
}

function withoutEmpty<Field extends string>(errors: FieldErrors<Field>): FieldErrors<Field> {
  return Object.fromEntries(Object.entries(errors).filter(([, message]) => message)) as FieldErrors<Field>;
}

export function validateAccessRequest(fields: AccessRequestFields): FieldErrors<keyof AccessRequestFields> {
  return withoutEmpty<keyof AccessRequestFields>({
    name: validateName(fields.name),
    email: validateEmail(fields.email),
    about: fields.about.trim().length > MAX_ABOUT_LENGTH ? `Please keep this to ${MAX_ABOUT_LENGTH} characters.` : undefined,
    consent: fields.consent ? undefined : "Please tick this so we know you're happy for us to get in touch.",
  });
}

export function validateContactMessage(fields: ContactMessageFields): FieldErrors<keyof ContactMessageFields> {
  return withoutEmpty<keyof ContactMessageFields>({
    name: validateName(fields.name),
    email: validateEmail(fields.email),
    message: !fields.message.trim()
      ? "Please write us a message."
      : fields.message.trim().length > MAX_MESSAGE_LENGTH
        ? `Please keep your message to ${MAX_MESSAGE_LENGTH} characters.`
        : undefined,
  });
}
